import React, { useState, useEffect, useMemo, useContext } from 'react';

import { useTranslation } from 'react-i18next';

import BdsButton from '../../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import DropdownButton from '../../../../../../shared/components/v3/Buttons/DropdownButton/DropdownButton';
import { CHECK_CIRCLE_V2, XERROR_ICON } from '../../../../../../shared/constants/images';
import { FLEXIBLE_REMUNERATION_REQUEST_STATUS } from '../../../../../../shared/constants/requests';
import { CompanyContext } from '../../../../../../shared/context/company/companyContext';
import { PlansContext } from '../../../../../../shared/context/plans/plansContext';
import { IFlexibleRemunerationRequest } from '../../../../../../shared/interfaces/company';
import { TRANSLATIONS_REQUESTS } from '../../../../../../shared/translations/requests';
import { GenericVoidFunction } from '../../../../../../shared/types/genericTypes';
import { getRemainingCycles } from '../../../../../../shared/utils/flexibleRemuneration/payrollMonths';
import { toLocaleNumberFormat, toLocalDateString } from '../../../../../../shared/utils/utils';
import './MaterialRequestDiscounts.scss';

const getShiftedMonthString = (rawYearMonth: string, monthOffset = 0): string => {
  const [yearStr, monthStr] = rawYearMonth.split('-');
  const date = new Date(Number(yearStr), Number(monthStr) - 1 + monthOffset, 1);

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');

  return `${yyyy}-${mm}-01`;
};

interface IMaterialRequestDiscountsProps {
  request: IFlexibleRemunerationRequest;
  onApprove: (payments: { month: string; amount: number }[]) => void;
  onReject: GenericVoidFunction;
}

const MaterialRequestDiscounts = ({ request, onApprove, onReject }: IMaterialRequestDiscountsProps) => {
  const { t } = useTranslation();
  const { locale: companyLocale, companyRFCycles } = useContext(CompanyContext);
  const { modelType } = useContext(PlansContext);
  const { amount: totalAmount, status, currency } = request;

  const remainingMonths = useMemo(() => {
    if (status !== FLEXIBLE_REMUNERATION_REQUEST_STATUS.pending.value) return [];
    return getRemainingCycles({ companyRFCycles, companyLocale, modelType });
  }, [companyLocale, companyRFCycles, status, modelType]);

  const [selectedMonth, setSelectedMonth] = useState<string>(remainingMonths[0]?.displayName || '');
  const formattedTotalAmount = useMemo(
    () =>
      toLocaleNumberFormat(
        totalAmount,
        { style: 'currency', currency: currency, maximumFractionDigits: 2 },
        companyLocale || undefined
      ),
    [totalAmount, companyLocale, currency]
  );

  const nonPendingMonthName =
    status === FLEXIBLE_REMUNERATION_REQUEST_STATUS.pending.value
      ? ''
      : (() => {
          const months = request.details.payments?.map((p) => p.month).filter((m) => /^\d{4}-\d{2}$/.test(m)) || [];
          if (!months.length) return '-';
          const monthDates = months.map((raw) => ({ raw, date: new Date(`${raw}-01T00:00:00Z`) }));
          const currentYear = new Date().getFullYear();
          const currentYearMonths = monthDates.filter((m) => m.date.getFullYear() === currentYear);
          const pickLatest = (arr: typeof monthDates) => arr.reduce((a, b) => (b.date > a.date ? b : a), arr[0]);
          const pickEarliest = (arr: typeof monthDates) => arr.reduce((a, b) => (b.date < a.date ? b : a), arr[0]);
          const target = currentYearMonths.length ? pickLatest(currentYearMonths) : pickEarliest(monthDates);

          const offset = modelType === 'forecast' ? -1 : 0;
          const dateString = getShiftedMonthString(target.raw, offset);
          return toLocalDateString(dateString, false, 'long');
        })();

  useEffect(() => {
    if (!remainingMonths.length) {
      setSelectedMonth('');
      return;
    }
    if (!selectedMonth || !remainingMonths.some((m) => m.displayName === selectedMonth)) {
      setSelectedMonth(remainingMonths[0].displayName);
    }
  }, [remainingMonths, selectedMonth, request.details.payments]);

  const callApprove = () => {
    const monthData = remainingMonths.find((m) => m.displayName === selectedMonth);
    if (!monthData) return;
    const yyyy = monthData.year;
    const mm = String(monthData.index).padStart(2, '0');
    const payload = [{ month: `${yyyy}-${mm}`, amount: totalAmount }];
    onApprove(payload);
  };

  return (
    <div className="flex flex-col gap-11" data-experience="new" data-testid="material-request-discounts">
      <div className="material-request-discount">
        <div className="mrd-title">{t(TRANSLATIONS_REQUESTS.details.materialDiscounts.title)}</div>
        <div className="mrd-content">
          <div className="mrd-field">
            <p className="label">{t(TRANSLATIONS_REQUESTS.details.materialDiscounts.totalAmountLabel)}</p>
            <div className="mrd-value-box">
              <p className="value">{formattedTotalAmount}</p>
            </div>
          </div>
          <div className="mrd-field">
            <p className="label">{t(TRANSLATIONS_REQUESTS.details.materialDiscounts.paymentMonthLabel)}</p>
            {status === FLEXIBLE_REMUNERATION_REQUEST_STATUS.pending.value ? (
              <DropdownButton
                selectionTitle={t(TRANSLATIONS_REQUESTS.details.materialDiscounts.paymentMonthLabel)}
                defaultOption={selectedMonth}
                options={remainingMonths.map((m) => ({ name: m.displayName, value: m.displayName }))}
                onChange={(opt) => {
                  setSelectedMonth(opt.value);
                }}
                isExpandable={true}
                customClass="mrd-dropdown"
              />
            ) : (
              <p className="value">{nonPendingMonthName}</p>
            )}
          </div>
        </div>
      </div>
      {status === FLEXIBLE_REMUNERATION_REQUEST_STATUS.pending.value && (
        <div className="flex justify-end gap-11">
          <BdsButton variant="tertiary" size="m" action={onReject} iconLeft={XERROR_ICON} disabled={!selectedMonth}>
            {t(TRANSLATIONS_REQUESTS.details.deductionsCard.rejectButton)}
          </BdsButton>
          <BdsButton
            variant="primary"
            size="m"
            action={callApprove}
            iconLeft={CHECK_CIRCLE_V2}
            disabled={!selectedMonth}
          >
            {t(TRANSLATIONS_REQUESTS.details.deductionsCard.approveButton)}
          </BdsButton>
        </div>
      )}
    </div>
  );
};

export default MaterialRequestDiscounts;
